import React, { useState, useEffect } from 'react';
import { db } from '../../firebase';
import { doc, getDoc, updateDoc, setDoc } from 'firebase/firestore';
import { linkedinOAuth } from '../../services/oauth/linkedinOAuth';
import { facebookOAuth } from '../../services/oauth/facebookOAuth';
import { twitterOAuth } from '../../services/oauth/twitterOAuth';
import { pinterestOAuth } from '../../services/oauth/pinterestOAuth';
import { isOAuthConfigured } from '../../config/oauth.config';
import { softPlatformIcon } from './socialSoftPalette';
import { LinkedInIcon, FacebookIcon, InstagramIcon, TwitterIcon, PinterestIcon, PremiumIcon, QuickIcon } from '../icons';
// import './SocialConnections.css';

interface ConnectionStatus {
  connected: boolean;
  connectedAt?: Date;
  username?: string;
  profileUrl?: string;
  expiresAt?: Date;
}

interface SocialConnectionsProps {
  userId: string;
}

const SocialConnections: React.FC<SocialConnectionsProps> = ({ userId }) => {
  const [connections, setConnections] = useState<Record<string, ConnectionStatus>>({});
  const [loading, setLoading] = useState(true);

  const [connectingPlatform, setConnectingPlatform] = useState<string | null>(null);
  const [disconnectingPlatform, setDisconnectingPlatform] = useState<string | null>(null);
  const [confirmDisconnect, setConfirmDisconnect] = useState<{ show: boolean; platformId: string | null }>({ show: false, platformId: null });
  const [successMessage, setSuccessMessage] = useState<string | null>(null);

  const platforms = [
    { id: 'linkedin', name: 'LinkedIn', Icon: LinkedInIcon, color: softPlatformIcon('linkedin'), requirement: 'Persoonlijk of bedrijfsprofiel' },
    { id: 'facebook', name: 'Facebook', Icon: FacebookIcon, color: softPlatformIcon('facebook'), requirement: 'Vereist: Business Page (geen persoonlijk profiel)' },
    { id: 'instagram', name: 'Instagram', Icon: InstagramIcon, color: softPlatformIcon('instagram'), requirement: 'Vereist: Business of Creator account' },
    { id: 'twitter', name: 'Twitter / X', Icon: TwitterIcon, color: softPlatformIcon('twitter'), requirement: 'Persoonlijk of bedrijfsaccount' },
    { id: 'pinterest', name: 'Pinterest', Icon: PinterestIcon, color: softPlatformIcon('pinterest'), requirement: 'Persoonlijk of zakelijk account' },
  ];

  const loadConnections = async () => {
    setLoading(true);
    try {
      const docRef = doc(db, 'socialConnections', userId);
      const docSnap = await getDoc(docRef);
      
      let connectionsData: Record<string, ConnectionStatus> = {};
      
      if (docSnap.exists()) {
        const data = docSnap.data();
        
        platforms.forEach(platform => {
          if (data[platform.id]) {
            connectionsData[platform.id] = {
              ...data[platform.id],
              connectedAt: data[platform.id].connectedAt?.toDate(),
              expiresAt: data[platform.id].expiresAt?.toDate()
            };
          } else {
            connectionsData[platform.id] = { connected: false };
          }
        });
      } else {
        // Initialize empty connections
        platforms.forEach(platform => {
          connectionsData[platform.id] = { connected: false };
        });
      }
      
      // Check localStorage for LinkedIn connection (from OAuth callback)
      const linkedinConnection = localStorage.getItem('linkedin_connection');
      if (linkedinConnection) {
        try {
          const connection = JSON.parse(linkedinConnection);
          connectionsData.linkedin = {
            connected: true,
            connectedAt: new Date(connection.connectedAt),
            username: `${connection.profile.firstName} ${connection.profile.lastName}`,
            profileUrl: connection.profile.profilePicture,
            expiresAt: new Date(Date.now() + (connection.expires_in * 1000))
          };
          
          // Save to Firestore
          await setDoc(docRef, {
            linkedin: {
              connected: true,
              connectedAt: new Date(connection.connectedAt),
              username: `${connection.profile.firstName} ${connection.profile.lastName}`,
              profileUrl: connection.profile.profilePicture,
              expiresAt: new Date(Date.now() + (connection.expires_in * 1000))
            }
          }, { merge: true });
          
          // Clear localStorage
          localStorage.removeItem('linkedin_connection');
        } catch (error) {
          console.error('Error parsing LinkedIn connection:', error);
        }
      }
      
      setConnections(connectionsData);
    } catch (error) {
      console.error('Error loading connections:', error);
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    loadConnections();
  }, [userId]);

  const handleConnect = async (platformId: string) => {
    setConnectingPlatform(platformId);
    
    try {
      if (platformId === 'linkedin') {
        // Check if user is authenticated
        if (!userId) {
          alert('⚠️ Je moet eerst inloggen voordat je LinkedIn kunt koppelen.\n\nLog in met Google of email en probeer het opnieuw.');
          setConnectingPlatform(null);
          return;
        }
        
        // Check if LinkedIn OAuth is configured
        if (!isOAuthConfigured('linkedin')) {
          alert('⚠️ LinkedIn OAuth is nog niet geconfigureerd.\n\nVoeg je LinkedIn Client ID en Client Secret toe aan .env.local\n\nVolg de setup instructies hieronder voor meer details.');
          setConnectingPlatform(null);
          return;
        }
        
        // Start LinkedIn OAuth flow
        console.log('🚀 Starting LinkedIn OAuth flow...');
        console.log('👤 User ID:', userId);
        
        // Save current location to return here after OAuth
        sessionStorage.setItem('linkedinReturnUrl', window.location.hash || '#social-media');
        
        linkedinOAuth.startOAuthFlow();
        // Note: User will be redirected to LinkedIn, no need to reset state here
        
      } else if (platformId === 'facebook' || platformId === 'instagram') {
        // Facebook/Instagram OAuth (Instagram via Facebook)
        if (!userId) {
          alert('⚠️ Je moet eerst inloggen voordat je Facebook kunt koppelen.');
          setConnectingPlatform(null);
          return;
        }
        
        if (!isOAuthConfigured('facebook')) {
          alert('⚠️ Facebook OAuth is nog niet geconfigureerd.\n\nVoeg je Facebook App ID en App Secret toe aan .env.local');
          setConnectingPlatform(null);
          return;
        }
        
        console.log('🚀 Starting Facebook OAuth flow...');
        sessionStorage.setItem('facebookReturnUrl', window.location.hash || '#social-media');
        
        // Start Facebook OAuth (handles both Facebook & Instagram)
        facebookOAuth.startOAuthFlow();
        
        // Listen for OAuth callback
        const handleMessage = async (event: MessageEvent) => {
          if (event.origin !== window.location.origin) return;
          
          if (event.data.type === 'FACEBOOK_OAUTH_SUCCESS') {
            console.log('✅ Facebook OAuth success received');
            try {
              await facebookOAuth.handleCallback(event.data.code, event.data.state);
              await loadConnections();
              setSuccessMessage('Facebook succesvol verbonden! 🎉');
              setTimeout(() => setSuccessMessage(null), 5000);
            } catch (error: any) {
              console.error('Error handling Facebook callback:', error);
              alert('Er is een fout opgetreden: ' + error.message);
            } finally {
              setConnectingPlatform(null);
            }
          } else if (event.data.type === 'FACEBOOK_OAUTH_ERROR') {
            console.error('❌ Facebook OAuth error:', event.data.error);
            alert('Facebook verbinding mislukt: ' + event.data.error);
            setConnectingPlatform(null);
          }
        };
        
        window.addEventListener('message', handleMessage);
        
        // Cleanup listener after 5 minutes
        setTimeout(() => {
          window.removeEventListener('message', handleMessage);
          if (connectingPlatform === platformId) {
            setConnectingPlatform(null);
          }
        }, 5 * 60 * 1000);
        
      } else if (platformId === 'twitter') {
        // Twitter/X OAuth
        if (!userId) {
          alert('⚠️ Je moet eerst inloggen voordat je Twitter kunt koppelen.');
          setConnectingPlatform(null);
          return;
        }
        
        if (!isOAuthConfigured('twitter')) {
          alert('⚠️ Twitter OAuth is nog niet geconfigureerd.\n\nVoeg je Twitter Client ID en Client Secret toe aan .env.local');
          setConnectingPlatform(null);
          return;
        }
        
        console.log('🚀 Starting Twitter OAuth flow...');
        sessionStorage.setItem('twitterReturnUrl', window.location.hash || '#social-media');
        
        twitterOAuth.startOAuthFlow();
        
      } else if (platformId === 'pinterest') {
        // Pinterest OAuth
        if (!userId) {
          alert('⚠️ Je moet eerst inloggen voordat je Pinterest kunt koppelen.');
          setConnectingPlatform(null);
          return;
        }
        
        if (!isOAuthConfigured('pinterest')) {
          alert('⚠️ Pinterest OAuth is nog niet geconfigureerd.\n\nVoeg je Pinterest App ID en App Secret toe aan .env.local');
          setConnectingPlatform(null);
          return;
        }
        
        console.log('🚀 Starting Pinterest OAuth flow...');
        sessionStorage.setItem('pinterestReturnUrl', window.location.hash || '#social-media');
        
        pinterestOAuth.startOAuthFlow();
        
      } else {
        // Other platforms not yet implemented
        alert(`OAuth setup voor ${platformId} is nog niet geconfigureerd.\n\nVolg de setup instructies om deze integratie te activeren.`);
        setConnectingPlatform(null);
      }
    } catch (error) {
      console.error('Error starting OAuth:', error);
      alert('Er is een fout opgetreden bij het starten van de OAuth flow.');
      setConnectingPlatform(null);
    }
  };

  const handleDisconnect = async (platformId: string) => {
    console.log('========================');
    console.log('🔌 handleDisconnect CALLED!');
    console.log('Platform:', platformId);
    console.log('User ID:', userId);
    console.log('Current connections:', connections);
    console.log('========================');
    
    // Show custom confirm modal
    setConfirmDisconnect({ show: true, platformId });
  };

  const confirmDisconnectAction = async () => {
    const platformId = confirmDisconnect.platformId;
    if (!platformId) return;
    
    console.log('✅ User confirmed disconnect!');
    setConfirmDisconnect({ show: false, platformId: null });
    
    setDisconnectingPlatform(platformId);
    console.log('⏳ Starting disconnect process...');
    
    try {
      // Immediately update UI to show disconnected state
      console.log('🔄 Updating UI state...');
      setConnections(prev => ({
        ...prev,
        [platformId]: {
          connected: false,
          disconnectedAt: new Date()
        }
      }));
      
      if (platformId === 'linkedin') {
        console.log('📤 Calling linkedinOAuth.disconnect...');
        await linkedinOAuth.disconnect(userId);
        console.log('✅ LinkedIn disconnect successful');
      } else if (platformId === 'facebook' || platformId === 'instagram') {
        console.log('📤 Calling facebookOAuth.disconnect...');
        await facebookOAuth.disconnect();
        console.log('✅ Facebook/Instagram disconnect successful');
      } else if (platformId === 'twitter') {
        console.log('📤 Calling twitterOAuth.disconnect...');
        await twitterOAuth.disconnect(userId);
        console.log('✅ Twitter disconnect successful');
      } else if (platformId === 'pinterest') {
        console.log('📤 Calling pinterestOAuth.disconnect...');
        await pinterestOAuth.disconnect(userId);
        console.log('✅ Pinterest disconnect successful');
      } else {
        console.log('📤 Updating Firestore...');
        const docRef = doc(db, 'socialConnections', userId);
        await updateDoc(docRef, {
          [platformId]: {
            connected: false,
            disconnectedAt: new Date()
          }
        });
        console.log('✅ Firestore update successful');
      }
      
      // Reload connections to confirm
      console.log('🔄 Reloading connections...');
      await loadConnections();
      
      // Show success message
      console.log(`✅ ${platformId} succesvol ontkoppeld`);
      setSuccessMessage(`✅ ${platformId.toUpperCase()} succesvol ontkoppeld!`);
      
      // Auto-hide success message after 5 seconds
      setTimeout(() => setSuccessMessage(null), 5000);
      
      // No alert or redirect - UI is already updated!
    } catch (error) {
      console.error('❌ Error disconnecting:', error);
      
      // Revert UI on error
      await loadConnections();
      
      // Show error notification in console only
      console.error('❌ Ontkoppelen mislukt:', error);
    } finally {
      setDisconnectingPlatform(null);
      console.log('✅ Disconnect process completed');
    }
  };

  const cancelDisconnect = () => {
    console.log('❌ User cancelled disconnect');
    setConfirmDisconnect({ show: false, platformId: null });
  };

  const [expandedSetup, setExpandedSetup] = useState<string | null>(null);

  if (loading) {
    return (
      <div className="flex items-center justify-center h-96">
        <div className="text-center">
          <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-[#7A9488] mx-auto mb-4"></div>
          <p className="text-gray-600">Connecties laden...</p>
        </div>
      </div>
    );
  }

  return (
    <div className="p-6 max-w-2xl mx-auto">
      {/* Success Message - Minimal toast */}
      {successMessage && (
        <div className="mb-6 p-3 bg-green-50 border border-green-200 rounded-lg flex items-center justify-between">
          <span className="text-green-700 text-sm">{successMessage}</span>
          <button onClick={() => setSuccessMessage(null)} className="text-green-500 hover:text-green-700">
            <svg className="w-4 h-4" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
        </div>
      )}
      
      {/* Header - Clean and simple */}
      <div className="mb-8">
        <h2 className="text-xl font-semibold text-gray-900">Accounts koppelen</h2>
        <p className="text-sm text-gray-500 mt-1">Verbind je social media voor automatisch delen</p>
      </div>

      {/* Platform List - Minimal cards */}
      <div className="space-y-3">
        {platforms.map(platform => {
          const connection = connections[platform.id];
          const isConnected = connection?.connected;

          return (
            <div 
              key={platform.id} 
              className="bg-white border border-gray-200 rounded-lg p-4 hover:border-gray-300 transition-colors"
            >
              <div className="flex items-center justify-between">
                {/* Platform info */}
                <div className="flex items-center gap-3">
                  <platform.Icon size={24} weight="regular" style={{ color: platform.color }} />
                  <div>
                    <h3 className="font-medium text-gray-900">{platform.name}</h3>
                    {isConnected && connection.username ? (
                      <p className="text-xs text-gray-500">@{connection.username}</p>
                    ) : (
                      <p className="text-xs text-gray-400">{platform.requirement}</p>
                    )}
                  </div>
                </div>

                {/* Status & Action */}
                <div className="flex items-center gap-3">
                  {isConnected ? (
                    <>
                      <span className="text-xs text-[#4F6660] font-medium">Gekoppeld</span>
                      <button
                        onClick={(e) => {
                          e.preventDefault();
                          e.stopPropagation();
                          if (connectingPlatform === platform.id) return;
                          handleDisconnect(platform.id);
                        }}
                        disabled={disconnectingPlatform === platform.id || connectingPlatform === platform.id}
                        className="text-xs text-gray-400 hover:text-[#4F6660] transition-colors disabled:opacity-50"
                      >
                        {disconnectingPlatform === platform.id ? 'Bezig...' : 'Ontkoppelen'}
                      </button>
                    </>
                  ) : (
                    <button
                      onClick={() => handleConnect(platform.id)}
                      disabled={connectingPlatform === platform.id}
                      className="px-4 py-1.5 bg-[#7A9488] text-white text-sm font-medium rounded-lg hover:bg-[#4F6660] transition-colors disabled:opacity-50"
                    >
                      {connectingPlatform === platform.id ? 'Bezig...' : 'Koppelen'}
                    </button>
                  )}
                </div>
              </div>

              {/* Connection details - only when connected */}
              {isConnected && connection.expiresAt && (
                <div className="mt-3 pt-3 border-t border-gray-100">
                  <p className="text-xs text-gray-400">
                    Verloopt: {connection.expiresAt.toLocaleDateString('nl-NL')}
                    <button
                      onClick={(e) => {
                        e.preventDefault();
                        e.stopPropagation();
                        if (disconnectingPlatform === platform.id) return;
                        handleConnect(platform.id);
                      }}
                      disabled={connectingPlatform === platform.id || disconnectingPlatform === platform.id}
                      className="ml-2 text-[#7A9488] hover:text-[#4F6660]"
                    >
                      {connectingPlatform === platform.id ? 'Bezig...' : 'Vernieuwen'}
                    </button>
                  </p>
                </div>
              )}
            </div>
          );
        })}
      </div>

      {/* Info sections */}
      <div className="mt-8 space-y-4">
        {/* Security */}
        <div className="p-4 bg-gray-50 rounded-lg">
          <h4 className="text-sm font-medium text-gray-700 mb-2">Veilige verbinding</h4>
          <ul className="text-xs text-gray-500 space-y-1">
            <li>• Geen wachtwoorden opgeslagen</li>
            <li>• Je kunt op elk moment ontkoppelen</li>
            <li>• Voldoet aan platform richtlijnen</li>
          </ul>
        </div>

        {/* Branding Info */}
        <div className="p-4 bg-stone-50 border border-stone-200 rounded-lg">
          <h4 className="text-sm font-medium text-gray-800 mb-2">Gratis account branding</h4>
          <p className="text-xs text-gray-700 mb-3">
            Als gratis gebruiker wordt automatisch branding toegevoegd aan je posts:
          </p>
          <div className="bg-white rounded p-3 text-xs text-gray-600 font-mono border border-stone-200">
            ---<br/>
            SuperKonnected | superkonnected.nl<br/>
            #SuperKonnected
          </div>
          <p className="text-xs text-[#4F6660] mt-3 font-medium flex items-center gap-1">
            <PremiumIcon size={14} weight="regular" /> Upgrade naar Premium om zonder branding te delen
          </p>
        </div>
      </div>

      {/* Confirm Disconnect Modal - Cleaner design */}
      {confirmDisconnect.show && (
        <div className="fixed inset-0 bg-black/40 flex items-center justify-center z-50 p-4">
          <div className="bg-white rounded-xl shadow-xl max-w-sm w-full p-6">
            <h3 className="text-lg font-semibold text-gray-900 mb-2">
              {platforms.find(p => p.id === confirmDisconnect.platformId)?.name} ontkoppelen?
            </h3>
            <p className="text-sm text-gray-500 mb-6">
              Je moet opnieuw koppelen om weer automatisch te kunnen delen.
            </p>
            <div className="flex gap-3">
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  cancelDisconnect();
                }}
                className="flex-1 px-4 py-2 border border-gray-200 text-gray-700 rounded-lg text-sm font-medium hover:bg-gray-50 transition-colors"
              >
                Annuleren
              </button>
              <button
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  confirmDisconnectAction();
                }}
                className="flex-1 px-4 py-2 bg-stone-600 text-white rounded-lg text-sm font-medium hover:bg-stone-700 transition-colors"
              >
                Ontkoppelen
              </button>
            </div>
          </div>
        </div>
      )}
    </div>
  );
};

export default SocialConnections;

